import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { BRAND_IDS } from '@nadoutong/contracts';
import type { BrandId } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useUpdateAccountSignupBrand } from '../api/customerScope.ts';
import { useT } from '../i18n/index.ts';
import { Button, IconButton, Select } from '../ui/index.ts';
import { brandLabel } from './BrandChip.tsx';
import { Modal } from './Modal.tsx';
import { useToast } from './Toast.tsx';

/**
 * Admin-only signup-brand correction dialog (epic #1030 E1-S4, #1040).
 *
 * Saves through `PUT /v1/ops/admin/accounts/:accountId/signup-brand`;
 * the platform guard refuses with `SIGNUP_BRAND_CONFLICT` while a
 * foreign-brand customer manager actively owns the customer, which this
 * dialog renders as an actionable inline message instead of a raw code.
 */
export function SignupBrandDialog({
  accountId,
  email,
  currentBrand,
  onClose,
}: {
  readonly accountId: string;
  readonly email: string;
  readonly currentBrand: BrandId;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const update = useUpdateAccountSignupBrand();
  const [selectedBrand, setSelectedBrand] = useState<string>(currentBrand);
  const unchanged = selectedBrand === currentBrand;

  async function handleSave(): Promise<void> {
    if (unchanged || update.isPending) return;
    try {
      await update.mutateAsync({ accountId, signupBrand: selectedBrand });
      toast.success(t('brand_edit.toast.saved'));
      onClose();
    } catch {
      // Inline mutation state renders the error.
    }
  }

  const errorMessage =
    update.error === null
      ? null
      : update.error instanceof OpsApiError && update.error.code === 'SIGNUP_BRAND_CONFLICT'
        ? t('brand_edit.error.conflict')
        : update.error instanceof OpsApiError
          ? `${update.error.code}: ${update.error.message}`
          : t('common.network_error');

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="signup-brand-dialog-title"
      testId="signup-brand-dialog"
    >
      <div className="flex flex-col gap-5">
        <header className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-label text-signal">{t('brand_edit.kicker')}</span>
            <h3 id="signup-brand-dialog-title" className="text-title text-paper">
              {t('brand_edit.title')}
            </h3>
          </div>
          <IconButton label={t('common.close')} onClick={onClose} className="shrink-0">
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </header>

        <div className="flex flex-col gap-1 rounded-lg border border-line-soft bg-card p-4">
          <span className="text-label text-paper-low">{t('brand_edit.customer')}</span>
          <span className="truncate text-body font-medium text-paper">{email}</span>
          <span className="font-mono text-dense text-paper-low">{accountId}</span>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-label text-paper-mid">{t('brand_edit.brand')}</span>
          <Select
            value={selectedBrand}
            onValueChange={setSelectedBrand}
            options={BRAND_IDS.map((brand) => ({ value: brand, label: brandLabel(brand) }))}
            ariaLabel={t('brand_edit.brand')}
            disabled={update.isPending}
            testId="signup-brand-dialog-select"
          />
          <span className="text-dense text-paper-low">{t('brand_edit.guard_hint')}</span>
        </div>

        {errorMessage !== null ? (
          <div
            role="alert"
            data-testid="signup-brand-dialog-error"
            className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-dense text-bad-bright"
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2 border-t border-line-soft pt-4">
          <Button type="button" onClick={onClose} disabled={update.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSave()}
            loading={update.isPending}
            disabled={unchanged || update.isPending}
            data-testid="signup-brand-dialog-save"
          >
            {update.isPending ? null : <Save aria-hidden="true" className="h-3.5 w-3.5" />}
            <span>{update.isPending ? t('common.saving') : t('common.save')}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
